import { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { RejectConfirmAppointment } from "./DataServices/AppointmentsDataServices";
import { fetchAutomaticResponseById, fetchAutomaticResponseHistoryById } from "./DataServices/AutomaticResponsesDataServices";
import { IAutomaticResponseHistory } from "./Models";
import { formatDateTime, formatTime } from "./AppointmentScheduleAndConfirmation";
import Loading from "./Loading";
import Notification from "./Notification";

export default function CancelConfirmation() {
  const [confirmationMessage, setConfirmationMessage] = useState(false);
  const [automaticResponseHistory, setAutomaticResponseHistory] = useState<IAutomaticResponseHistory>();
  const [loading, setLoading] = useState(false)
  const [openNotification, setOpenNotification] = useState(false)
  const { id } = useParams<{ id: string | undefined }>();
  const navigate = useNavigate();

  const cancelButtonFunction = async () => {
    try {
      setLoading(true)
      if (id) {
        await RejectConfirmAppointment([id], true);
        setConfirmationMessage(true);
      }
    } catch (error) {
      setConfirmationMessage(false);
      setOpenNotification(true)
      console.error("Error fetching appointment response:", error);
    }
    finally {
      setLoading(false)
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          const response = await fetchAutomaticResponseHistoryById(id);
          setAutomaticResponseHistory(response);
          
          if (response.rejected || response.cancelled) setConfirmationMessage(true)
        }
      } catch (error) {
        console.error("Error fetching:", error);
      }
    };

    fetchData();
  }, [id, confirmationMessage]);

  return (
    <>
      <div className="bg-gray-50 min-h-screen">
        <header className="bg-red-700 text-white py-4 px-6 shadow-xl">
          <h1 className="text-xl font-semibold">Cancel Your Appointment</h1>
          <p className="text-sm text-gray-100">Need to cancel? You can cancel your scheduled appointment here.</p>
        </header>

        <main className="px-4 py-8 md:px-16 lg:px-32 space-y-10">
          {confirmationMessage && (
            <div className="h-full flex items-center justify-center p-6">

              <div className="bg-white rounded-2xl shadow-md max-w-xl w-full p-8 text-gray-800">

                <div className="text-center mb-6">
                  <h1 className="text-xl font-extrabold text-indigo-700">Appointment Status</h1>
                  <p className="text-sm text-gray-500">Here are the details of your appointment</p>
                </div>

                <div className="flex justify-center mb-6">
                  <svg width="120" height="120" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <rect x="10" y="30" width="80" height="60" rx="12" fill="#F5F5F5" stroke="#CCCCCC" stroke-width="2" />
                    <rect x="10" y="10" width="80" height="30" rx="8" fill="#6366F1" />
                    <circle cx="20" cy="25" r="6" fill="white" stroke="#888" stroke-width="2" />
                    <circle cx="80" cy="25" r="6" fill="white" stroke="#888" stroke-width="2" />
                    <text x="50" y="30" font-size="14" text-anchor="middle" fill="white" font-family="Arial" font-weight="bold">{automaticResponseHistory ? new Date(automaticResponseHistory.startTime).toLocaleString('en-US', { month: 'short' }) : "Jan"}</text>
                    <text x="50" y="75" font-size="35" text-anchor="middle" fill="black" font-family="Arial" font-weight="bold">{automaticResponseHistory ? new Date(automaticResponseHistory.startTime).getDate() : 0}</text>
                  </svg>
                </div>

                <div className="text-center mb-4">
                  <div className="text-green-600 text-lg font-semibold">
                    Hey {automaticResponseHistory?.firstName}, your appointment<br /> has been cancelled successfully
                  </div>
                </div>
                <div className="text-center text-2xl font-bold text-gray-800 mb-2">{automaticResponseHistory?.title}</div>

                <div className="text-center text-indigo-600 text-sm mb-6">
                  {automaticResponseHistory && formatDateTime(automaticResponseHistory.startTime.toString()) + " "}
                  to {automaticResponseHistory && formatTime(automaticResponseHistory.endTime.toString())}
                </div>
                <div className="text-center text-gray-600 text-sm">
                  <p className="mb-2">
                    Need to make changes?{" "}
                    <a href={`/reschedule-confirmation/${id}`} className="text-indigo-600 underline hover:text-indigo-800">Click here to reschedule</a>.
                  </p>
                  <p>We look forward to seeing you!</p>
                </div>
              </div>

            </div>
          )}
          {(!confirmationMessage &&
            <>
              {automaticResponseHistory?<section className="bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-xl font-semibold mb-4">{automaticResponseHistory?.title}</h2>
                <p className="text-gray-700">
                  Your visit to the selected apartments has been confirmed. If you need to cancel the appointment, please click the "No, I cannot attend" button below. If you change your mind, you'll need to book a new appointment later.
                </p>
                <div className="text-start text-indigo-600 mt-2">
                  <span className="text-gray-600 font-medium mr-2">Scheduled Appointment:</span> {automaticResponseHistory && formatDateTime(automaticResponseHistory.startTime.toString()) + " to "}{automaticResponseHistory && formatTime(automaticResponseHistory.endTime.toString())}
                </div>
                <div className="flex w-full items-center justify-end mt-2 md:mt-0">
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-gray-50 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-red-500 sm:mt-0 sm:w-auto active:translate-y-[1px]"
                    onClick={cancelButtonFunction}
                  >
                    No, I cannot attend
                  </button>
                </div>
              </section>:
              <section className="bg-white p-6 rounded-md shadow-sm">
                <p className="text-gray-700">
                  This appointment no longer exists. If you believe this is a mistake, please contact support.
                </p>
              </section>}
            </>
          )}
        </main>
        {loading && <div className="absolute bg-gray-300/80 top-0 left-0 w-full h-full flex items-center justify-center">
          <Loading />
        </div>}
        <Notification
          isOpen={openNotification}
          setIsOpen={setOpenNotification}
          message={"Appointment cancellation failed!!"}
          messageType={"error"}
        />
      </div >
    </>

  );
}
